import { RoomEvent, Track } from 'livekit-client';
import type { Room, RemoteTrack, RemoteParticipant, RemoteTrackPublication } from 'livekit-client';
import type { EventBus } from '../event-bus';
import type { RoomManager } from '../room';

export interface AvatarBuiltinOptions {
  roomManager: RoomManager;
  eventBus: EventBus;
  /** External/advanced integration: a CSS selector resolved on the HOST
   * PAGE's document, looked up fresh each time a video track subscribes.
   * Falls back to this when `mountEl` is unset/null. */
  mountSelector?: string;
  /** Internal integration (shadow render mode): a direct element reference
   * living inside ChatPanel's own (closed) shadow root, e.g. from
   * `RenderAdapter.getAvatarMountEl()`. Takes precedence over
   * `mountSelector` when set. May start out `null` (panel collapsed / not
   * yet connected) and arrive later via `setMountEl` — see below. */
  mountEl?: HTMLElement | null;
  fit?: 'cover' | 'contain';
  opacity?: number;
}

export interface AvatarBuiltinHandle {
  /**
   * Re-resolve the internal mount element. Call this after every
   * presentation re-render (e.g. on every `patch()` in bootstrap.ts): the
   * internal `[data-mobeus-avatar-mount]` node only exists while the panel
   * is expanded and `avatarEnabled` is true, so it can appear, disappear,
   * and reappear as a *different* DOM node across the embed's lifetime
   * (React tears it down when the panel collapses). Any avatar video track
   * already subscribed but not yet attached (mount wasn't available at
   * subscribe time) gets attached now; any track already attached to a
   * stale/detached node gets re-attached to the new one. No-op if `el` is
   * unchanged and everything's already correctly attached.
   */
  setMountEl(el: HTMLElement | null): void;
  /**
   * Mute/unmute the audio this built-in is playing, on top of (not instead
   * of) the avatar-vs-direct-agent audio routing `applyAudioRouting` already
   * does — see that function for how the two combine.
   */
  setOutputMuted(muted: boolean): void;
}

/**
 * Avatar built-in.
 *
 * Subscribes to the LiveKit Room's TrackSubscribed / TrackUnsubscribed events.
 * For video tracks from an avatar worker participant (identified by the
 * non-empty `lk.publish_on_behalf` attribute) it creates a
 * `<video data-mobeus-avatar>` element inside the resolved mount target
 * (`mountEl`, falling back to `document.querySelector(mountSelector)`) and
 * attaches the LiveKit track.
 *
 * Audio routing: when an avatar audio track is active, the agent's direct
 * audio is muted so the avatar voices the agent. Mirrors the starter's
 * `applyAudioRouting` logic in `voice-session-store.ts`.
 */
export function installAvatarBuiltin(opts: AvatarBuiltinOptions): AvatarBuiltinHandle {
  const { roomManager, eventBus, mountSelector } = opts;
  const fit = opts.fit ?? 'cover';
  const opacity = typeof opts.opacity === 'number' ? opts.opacity : 1;

  // Track-keyed bookkeeping so unsubscribe handlers can find the right node.
  const videoElements = new Map<RemoteTrack, HTMLVideoElement>();
  const avatarAudioElements = new Map<RemoteTrack, HTMLAudioElement>();
  const agentAudioElements = new Map<RemoteTrack, HTMLAudioElement>();
  let attachedRoom: Room | null = null;

  // Avatar video tracks currently subscribed, regardless of whether a mount
  // was available to attach them to. Lets `setMountEl` retroactively attach
  // a track that subscribed while the panel was collapsed (or before the
  // internal mount div existed) once a mount finally shows up.
  const subscribedAvatarVideoTracks = new Set<RemoteTrack>();
  let currentMountEl: HTMLElement | null = opts.mountEl ?? null;
  // Output-mute override from bootstrap.ts's `toggleVolume` control. Layered
  // on top of the avatar-vs-direct-agent routing below, not a replacement
  // for it — see `applyAudioRouting`.
  let outputMuted = false;
  let mediaReadyEmitted = false;
  const playingVideos = new Set<RemoteTrack>();
  function emitMediaReady(): void {
    if (mediaReadyEmitted || playingVideos.size === 0 || avatarAudioElements.size === 0) return;
    mediaReadyEmitted = true;
    eventBus.emit('avatarMediaReady', {});
  }

  function resolveMount(): HTMLElement | null {
    if (currentMountEl) return currentMountEl;
    if (mountSelector) return document.querySelector(mountSelector) as HTMLElement | null;
    return null;
  }

  function attachVideoTrack(track: RemoteTrack, mount: HTMLElement): void {
    const existing = videoElements.get(track);
    if (existing && existing.isConnected && existing.parentElement === mount) return; // already correct
    if (existing) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(existing);
      } catch {
        /* ignore */
      }
      existing.remove();
      videoElements.delete(track);
    }
    const video = document.createElement('video');
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    video.setAttribute('data-mobeus-avatar', '');
    video.style.objectFit = fit;
    // Start invisible: a subscribed-but-not-yet-playing track renders as a
    // black box that covers the panel's `--mobeus-fs-bg` art / thumbnail
    // idle background. Fade to the configured opacity only once the stream
    // actually has frames ('playing'), so the idle art stays visible through
    // the avatar's multi-second cold start instead of a black void.
    video.style.opacity = '0';
    video.style.transition = 'opacity 0.5s ease';
    video.addEventListener(
      'playing',
      () => {
        if (videoElements.get(track) !== video) return;
        video.style.opacity = String(opacity);
        eventBus.emit('avatarReady', {});
        playingVideos.add(track);
        emitMediaReady();
      },
      { once: true },
    );
    video.style.width = '100%';
    video.style.height = '100%';
    mount.appendChild(video);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (track as any).attach(video);
    } catch (e) {
      console.warn('[Mobeus] avatar track.attach threw', e);
    }
    videoElements.set(track, video);
  }

  function tryAttachVideo(track: RemoteTrack): void {
    const mount = resolveMount();
    if (!mount) {
      console.warn('[Mobeus] avatar mount not available yet; will attach once it appears');
      return;
    }
    attachVideoTrack(track, mount);
  }

  function applyAudioRouting() {
    const avatarActive = avatarAudioElements.size > 0;
    for (const el of avatarAudioElements.values()) el.muted = outputMuted;
    // Mute the direct agent audio when the avatar is voicing the agent, OR
    // when output is muted globally — either condition alone is sufficient.
    for (const el of agentAudioElements.values()) el.muted = outputMuted || avatarActive;
  }

  function handleTrackSubscribed(
    track: RemoteTrack,
    _publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ): void {
    const publishOnBehalf = participant.attributes?.['lk.publish_on_behalf'];

    if (publishOnBehalf) {
      // Avatar worker.
      if (track.kind === Track.Kind.Video) {
        subscribedAvatarVideoTracks.add(track);
        tryAttachVideo(track);
      } else if (track.kind === Track.Kind.Audio) {
        let audioEl: HTMLAudioElement | null = null;
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          audioEl = (track as any).attach() as HTMLAudioElement;
        } catch (e) {
          console.warn('[Mobeus] avatar audio track.attach threw', e);
          return;
        }
        if (!audioEl) return;
        audioEl.autoplay = true;
        audioEl.setAttribute('data-mobeus-avatar-audio', '');
        document.body.appendChild(audioEl);
        avatarAudioElements.set(track, audioEl);
        applyAudioRouting();
        emitMediaReady();
      }
      return;
    }

    // Non-avatar participant (likely the agent itself). Capture agent audio
    // so we can mute it when the avatar voices the agent.
    if (track.kind === Track.Kind.Audio) {
      let audioEl: HTMLAudioElement | null = null;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        audioEl = (track as any).attach() as HTMLAudioElement;
      } catch (e) {
        console.warn('[Mobeus] agent audio track.attach threw', e);
        return;
      }
      if (!audioEl) return;
      audioEl.autoplay = true;
      audioEl.setAttribute('data-mobeus-agent-audio', '');
      document.body.appendChild(audioEl);
      agentAudioElements.set(track, audioEl);
      applyAudioRouting();
    }
  }

  function handleTrackUnsubscribed(track: RemoteTrack): void {
    subscribedAvatarVideoTracks.delete(track);
    playingVideos.delete(track);
    const video = videoElements.get(track);
    if (video) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(video);
      } catch (e) {
        console.warn('[Mobeus] avatar track.detach threw', e);
      }
      video.remove();
      videoElements.delete(track);
    }
    const avatarAudio = avatarAudioElements.get(track);
    if (avatarAudio) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(avatarAudio);
      } catch (e) {
        console.warn('[Mobeus] avatar audio track.detach threw', e);
      }
      avatarAudio.remove();
      avatarAudioElements.delete(track);
      applyAudioRouting();
    }
    const agentAudio = agentAudioElements.get(track);
    if (agentAudio) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(agentAudio);
      } catch (e) {
        console.warn('[Mobeus] agent audio track.detach threw', e);
      }
      agentAudio.remove();
      agentAudioElements.delete(track);
      applyAudioRouting();
    }
  }

  function teardownAllElements(): void {
    for (const [track, el] of videoElements) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(el);
      } catch {
        /* ignore */
      }
      el.remove();
    }
    videoElements.clear();
    playingVideos.clear();
    mediaReadyEmitted = false;
    subscribedAvatarVideoTracks.clear();
    for (const [track, el] of avatarAudioElements) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(el);
      } catch {
        /* ignore */
      }
      el.remove();
    }
    avatarAudioElements.clear();
    for (const [track, el] of agentAudioElements) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (track as any).detach(el);
      } catch {
        /* ignore */
      }
      el.remove();
    }
    agentAudioElements.clear();
  }

  eventBus.on('sessionStart', () => {
    const room = roomManager.getRoom();
    if (!room || attachedRoom === room) return;
    attachedRoom = room;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (room as any).on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (room as any).on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
  });

  eventBus.on('sessionEnd', () => {
    teardownAllElements();
    attachedRoom = null;
  });

  return {
    setMountEl(el) {
      currentMountEl = el;
      if (!el) return;
      for (const track of subscribedAvatarVideoTracks) {
        tryAttachVideo(track);
      }
    },
    setOutputMuted(muted) {
      outputMuted = muted;
      applyAudioRouting();
    },
  };
}
